/**
 * Trending ranking — client-side re-ranking of trending feed posts using a
 * time-decay weighted engagement score.
 *
 * The backend returns posts in a server-determined trending order. This
 * utility re-ranks them on the client using a more nuanced algorithm that
 * prioritises recent engagement velocity over total historical counts, so
 * the feed feels current and dynamic.
 *
 * Score = (likes + 2 * comments + 3 * shares) * decayFactor
 *
 * The decay factor uses a half-life of 12 hours: a post loses half its score
 * every 12 hours unless it is still receiving engagement. Posts older than
 * 72 hours with low engagement naturally fall to the bottom.
 */

import type { FeedPost } from '../types/post';
import { parseToDate } from './dateHelpers';

/** Half-life for time decay — posts lose half their score every 12 hours. */
const DECAY_HALF_LIFE_HOURS = 12;

/** Posts older than this are effectively zero-scored unless engagement is high. */
const MAX_RELEVANCE_HOURS = 72;

/** Weight: comments are worth 2x a like, shares 3x (they require more effort). */
const COMMENT_WEIGHT = 2;
const SHARE_WEIGHT = 3;

/**
 * Parses a post date (e.g. "2026-08-14T05:22:01") or a relative time string
 * into a timestamp. Falls back to Date.now() - 1 hour for unparseable values
 * so they get a neutral score.
 *
 * Feed reads send `timezoneOffset`, so a `postDate` with no timezone
 * designator already carries the caller's local wall-clock time (`'local'`).
 * Reading it as UTC would place recent posts in the future and let the decay
 * curve boost them above their real engagement.
 */
function parsePostTimestamp(postDate: string | undefined, timeAgo: string): number {
  if (postDate) {
    const parsed = parseToDate(postDate, 'local');
    if (parsed) return parsed.getTime();
  }
  // Try to estimate from timeAgo string (e.g., "2 hours ago", "Just now")
  const now = Date.now();
  const hourMatch = timeAgo.match(/(\d+)\s*h/i);
  if (hourMatch) return now - parseInt(hourMatch[1], 10) * 3600_000;
  const dayMatch = timeAgo.match(/(\d+)\s*d/i);
  if (dayMatch) return now - parseInt(dayMatch[1], 10) * 86_400_000;
  const minMatch = timeAgo.match(/(\d+)\s*m/i);
  if (minMatch) return now - parseInt(minMatch[1], 10) * 60_000;
  return now - 3600_000; // Default: 1 hour ago
}

/**
 * Computes a trending score for a single post. Higher = more trending.
 * Returns 0 for posts that are too old with no meaningful engagement.
 */
export function computeTrendingScore(post: FeedPost, now: number = Date.now()): number {
  const timestamp = parsePostTimestamp(post.postDate, post.timeAgo);
  // Clamped: a timestamp slightly ahead of the local clock would otherwise
  // give a decay factor above 1 and inflate the score.
  const ageHours = Math.max(0, (now - timestamp) / 3600_000);

  // Posts older than the max relevance window with low engagement get ~0.
  if (ageHours > MAX_RELEVANCE_HOURS && post.likes + post.comments < 5) {
    return 0;
  }

  // Engagement score: weighted sum of likes, comments, shares.
  const engagement = post.likes + COMMENT_WEIGHT * post.comments + SHARE_WEIGHT * post.shares;

  // Time decay: exponential decay with a 12-hour half-life.
  // decayFactor = 0.5^(ageHours / halfLifeHours)
  const decayFactor = Math.pow(0.5, ageHours / DECAY_HALF_LIFE_HOURS);

  return engagement * decayFactor;
}

/**
 * Re-ranks an array of feed posts by trending score (highest first).
 * Deduplicates by post ID. Posts scoring 0 (stale, no recent engagement) sink
 * to the bottom but are kept: the server already curated the list as trending,
 * so dropping them would leave holes in the feed.
 */
export function rankTrendingPosts(posts: FeedPost[]): FeedPost[] {
  if (posts.length <= 1) return posts;

  const seen = new Set<string>();
  const unique = posts.filter(p => {
    if (seen.has(p.id)) return false;
    seen.add(p.id);
    return true;
  });

  const now = Date.now();
  const scored = unique.map(post => ({
    post,
    score: computeTrendingScore(post, now),
  }));

  // Sort by score descending. Posts with score 0 go to the bottom but
  // are not removed (the server already curated them as trending).
  scored.sort((a, b) => b.score - a.score);

  return scored.map(s => s.post);
}
